import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Image as ImageIcon, Loader2, Send, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtSize, shareUrl, waLink, vehicleLabel } from "@/lib/api";
import FileDropzone from "@/components/FileDropzone";

function FileIcon({ type }) {
  if (type.startsWith("image/")) return <ImageIcon size={18} className="text-sky-400" />;
  if (type.startsWith("video/")) return <Video size={18} className="text-amber-400" />;
  return <FileText size={18} className="text-[#E50914]" />;
}

function FileRow({ f, ticket, isAdmin, onDelete }) {
  const text = `[Supporto Tecnico] Documento per Ticket #${ticket.number} - ${vehicleLabel(ticket.vehicle)} (${ticket.vehicle.plate || ticket.vehicle.vin}): ${f.original_filename}\nScarica/visualizza: ${shareUrl(f.share_token)}`;
  return (
    <div className="surface-2 p-3 flex flex-wrap items-center gap-3" data-testid={`file-row-${f.id}`}>
      <FileIcon type={f.content_type} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate" data-testid="file-name">{f.original_filename}</div>
        <div className="text-xs text-slate-500 mono">{fmtSize(f.size)} · {new Date(f.created_at).toLocaleString("it-IT")}</div>
      </div>
      <div className="flex gap-2">
        <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="btn-ghost p-2" title="Visualizza" data-testid={`view-file-${f.id}`}>
          <Eye size={16} />
        </a>
        <a href={fileUrl(f.id, true)} className="btn-ghost p-2" title="Scarica" data-testid={`download-file-${f.id}`}>
          <Download size={16} />
        </a>
        {isAdmin && f.kind === "document" && (
          <>
            <a href={waLink(ticket.client_phone, text)} target="_blank" rel="noreferrer"
              className="btn-wa px-3 py-2 text-xs flex items-center gap-1.5" data-testid="send-doc-whatsapp-button">
              <Send size={14} /> Invia Documento su WhatsApp
            </a>
            <button onClick={() => onDelete(f)} className="btn-ghost p-2 hover:!border-red-500" title="Rimuovi" data-testid={`delete-file-${f.id}`}>
              <Trash2 size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, files, empty, testid, children, ...rowProps }) {
  return (
    <div data-testid={testid}>
      <h3 className="font-display text-lg uppercase font-bold mb-3">{title} <span className="mono text-sm text-slate-500">({files.length})</span></h3>
      {children}
      <div className="space-y-2 mt-3">
        {files.length ? files.map((f) => <FileRow key={f.id} f={f} {...rowProps} />) : <div className="text-sm text-slate-500">{empty}</div>}
      </div>
    </div>
  );
}

export default function DocumentRepository({ ticket, isAdmin }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/tickets/${ticket.id}/files`).then(({ data }) => setFiles(data)).catch(() => {});
  }, [ticket.id]);
  useEffect(() => {
    load();
  }, [load]);

  const upload = async (list) => {
    setBusy(true);
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/tickets/${ticket.id}/files`, fd);
      toast.success("File caricati");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (f) => {
    if (!window.confirm(`Rimuovere ${f.original_filename}?`)) return;
    await api.delete(`/files/${f.id}`);
    load();
  };
  const docs = files.filter((f) => f.kind === "document");
  const atts = files.filter((f) => f.kind === "attachment");
  const props = { ticket, isAdmin, onDelete: remove };
  const dropzone = (title, hint, accept) => (
    <div className="relative">
      <FileDropzone testid={isAdmin ? "admin-docs-dropzone" : "client-attachments-dropzone"} accept={accept} onFiles={upload} title={title} hint={hint} />
      {busy && <div className="absolute inset-0 bg-black/70 grid place-items-center rounded-md"><Loader2 className="animate-spin text-[#E50914]" /></div>}
    </div>
  );
  return (
    <div className="space-y-8">
      <Section title="Repository Documenti Tecnici" files={docs} testid="tech-docs-section" {...props}
        empty={isAdmin ? "Nessun documento caricato." : "Il tecnico non ha ancora condiviso documenti."}>
        {isAdmin && dropzone("Trascina qui PDF, schemi elettrici, pinout o report", "PDF o immagini · max 50MB", "application/pdf,image/*")}
      </Section>
      <Section title="Foto / Video del cliente" files={atts} testid="client-attachments-section" {...props} empty="Nessun allegato.">
        {!isAdmin && dropzone("Aggiungi altre foto o video", "Immagini o video · max 50MB", "image/*,video/*")}
      </Section>
    </div>
  );
}
